"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import MobileMenuButton from "@/components/MobileMenuButton";
import { ArrowRight, X } from "lucide-react";
import { LanguageSelector } from "@/components/ui/language-selector";
import { useTranslation } from "react-i18next";
import { AnimatedBackground } from "@/components/core/animated-background";
import clsx from "clsx";

interface LandingPageNavigationProps {
  onGetStarted?: () => void;
  onSignIn?: () => void;
  isAuthenticating?: boolean;
}

export function LandingPageNavigation({ onGetStarted, onSignIn, isAuthenticating = false }: LandingPageNavigationProps) {
  const { t } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [navigationError, setNavigationError] = useState<string | null>(null);

  const navLinks = [
    // { href: "/#overview", label: t('nav.overview') },
    { href: "/#features", label: t('nav.features') },
    { href: "/#Platforms", label: t('nav.platforms') },
    { href: "/#pricing", label: t('nav.pricing') },
    { href: "/#about", label: t('nav.about') },
  ];

  const [activeLink, setActiveLink] = useState('');
  const isClickScrollingRef = useRef(false);
  const clickScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const header = document.querySelector('header');
    const navHeight = header ? header.offsetHeight : 70;

    // scroll-mt-32 = 128px; threshold must exceed this so sections register as active when scrolled-to
    const scrollThreshold = navHeight + 80;

    const handleScroll = () => {
      if (isClickScrollingRef.current) return;
      let newActive = '';

      const heroSection = document.getElementById('hero');
      if (heroSection) {
        const heroRect = heroSection.getBoundingClientRect();
        if (heroRect.bottom > scrollThreshold) {
          setActiveLink('');
          return;
        }
      }

      for (const link of [...navLinks].reverse()) {
        const sectionId = link.href.substring(2);
        const section = document.getElementById(sectionId);
        if (section) {
          const rect = section.getBoundingClientRect();
          if (rect.top <= scrollThreshold) {
            newActive = link.label;
            break;
          }
        }
      }

      setActiveLink(newActive);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [navLinks]);

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    return () => document.body.classList.remove('overflow-hidden');
  }, [isMobileMenuOpen]);

  const handleGetStarted = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (isNavigating) return;
    setIsNavigating(true);
    setNavigationError(null);
    try {
      if (onGetStarted) {
        onGetStarted();
      }
    } catch (error) {
      console.error('Navigation error:', error);
      setNavigationError(error instanceof Error ? error.message : 'Failed to navigate. Please try again.');
    } finally {
      if (!onGetStarted) {
        setIsNavigating(false);
      }
    }
  };

  const renderUnauthenticatedNav = () => (
    <>
      <div className="hidden md:flex items-center w-full">
        <div className="flex-1 flex justify-start">
          <Link href="/" className="flex items-center h-full">
            <Image src="/favicon.ico" alt="Lema Logo" className="h-9 w-9" width={36} height={36} />
          </Link>
        </div>
        <nav className="flex-1 flex justify-center items-center">
          <AnimatedBackground
            value={activeLink}
            className='gap-x-6'
            transition={{
              type: 'spring',
              bounce: 0.2,
              duration: 0.6,
            }}
          >
            {navLinks.map(link => (
              <Link 
                key={link.href} 
                href={link.href} 
                data-id={link.label}
                onClick={() => {
                  setActiveLink(link.label);
                  isClickScrollingRef.current = true;
                  if (clickScrollTimeoutRef.current) clearTimeout(clickScrollTimeoutRef.current);
                  clickScrollTimeoutRef.current = setTimeout(() => {
                    isClickScrollingRef.current = false;
                  }, 1000);
                }}
                className={clsx(
                  "relative z-10 px-5 py-1.5 text-base rounded-full transition-colors",
                  link.label === activeLink 
                    ? "text-black" 
                    : "text-neutral-300 hover:bg-neutral-800/90"
                )}
              >
                {link.label}
              </Link>
            ))}
          </AnimatedBackground>
        </nav>
        <div className="flex-1 flex justify-end items-center space-x-4">
          <LanguageSelector />
          <div className="flex flex-col items-start">
            {navigationError && (
              <p className="text-red-400 text-xs mb-1">{navigationError}</p>
            )}
            <Button
              onClick={handleGetStarted}
              className="bg-highlight text-highlight-foreground hover:bg-highlight/90 px-6 py-2 text-sm font-medium disabled:opacity-70 disabled:cursor-not-allowed transition-opacity rounded-lg"
              disabled={isAuthenticating || isNavigating}
            >
              {t('nav.getStarted')}
            </Button>
          </div>
        </div>
      </div>
      
      <div className="md:hidden flex items-center justify-between w-full">
        <Link href="/" className="flex items-center">
          <Image src="/favicon.ico" alt="Lema Logo" width={28} height={28} className="h-7 w-7" />
        </Link>
        <div className="flex items-center gap-3">
          <Button
            onClick={handleGetStarted}
            className="bg-highlight text-highlight-foreground hover:bg-highlight/90 px-4 py-1.5 text-xs font-medium disabled:opacity-70 disabled:cursor-not-allowed transition-opacity rounded-lg h-8"
            disabled={isAuthenticating || isNavigating}
          >
            {t('nav.getStarted')}
          </Button>
          <MobileMenuButton
            onClick={toggleMobileMenu}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
            srLabel={t('nav.openMainMenu')}
            lineColorClassName="bg-neutral-300"
          />
        </div>

        {isMobileMenuOpen && (
          <div
            className="fixed inset-0 z-[9999] bg-black text-foreground flex flex-col"
            role="dialog"
            aria-modal="true"
            id="mobile-menu"
          >
            <div className="h-14 px-4 py-2 flex items-center justify-between">
              <Link href="/" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center">
                <Image src="/favicon.ico" alt="Lema Logo" width={28} height={28} className="h-7 w-7" />
              </Link>
              <button
                className="h-9 w-9 flex items-center justify-center rounded-md"
                aria-label={t('nav.closeMenu')}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <X className="h-10 w-10" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <nav className="px-4">
                <ul className="divide-y divide-border">
                  {navLinks.map(link => (
                    <li key={link.href}>
                      <Link href={link.href} onClick={() => setIsMobileMenuOpen(false)} className="block py-5 text-2xl font-medium flex items-center justify-between">
                        <span>{link.label}</span>
                        <ArrowRight className="h-6 w-6" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>

            <div className="px-4 pb-4 pt-2 space-y-3 bg-background">
              <Link href="/auth/sign-up" onClick={() => setIsMobileMenuOpen(false)} className="block w-full">
                <Button variant="highlight" className="w-full h-12 text-base font-medium">{t('nav.getStarted')}</Button>
              </Link>
              <div className="pt-1 pb-6">
                <LanguageSelector variant="mobile" onLanguageChange={() => setIsMobileMenuOpen(false)} />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );

  return (
    <header
      className={clsx(
        "w-full fixed top-0 z-50",
        isMobileMenuOpen ? "bg-black" : "bg-background/65 backdrop-blur-md",
      )}
    >
      <div className="h-14 sm:h-16 px-4 md:px-6 w-full flex items-center">
        {renderUnauthenticatedNav()}
      </div>
    </header>
  );
}
